import {
  Badge,
  Box,
  ButtonIcon,
  Cell,
  type ColumnConfig,
  Link,
  Flex,
  SearchField,
  type SortDescriptor,
  Table,
  Tag,
  TagGroup,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import type { SeverityDto, TeamProjectDto } from '@internal/scorecards-common';
import {
  entityRouteParams,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';

import { useMemo, useState } from 'react';

import { RiInformationLine } from '@remixicon/react';
import { PassFailBar } from '../SegmentedBar/SegmentedBar';
import { StatusBadge } from '../StatusBadge/StatusBadge';
import { severityDisplay } from '../../severity';
import { useSeverities } from '../../hooks/useSeverities';
import {
  failingCountsBySeverity,
  matchesSearch,
  passPct,
} from './projectFilters';
import styles from './ProjectsTable.module.css';
import { useRouteRef } from '@backstage/core-plugin-api';

type ProjectRow = TeamProjectDto & { id: string };

const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const relativeTimeFormat = new Intl.RelativeTimeFormat(undefined, {
  numeric: 'always',
});

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function formatAge(iso: string, now = Date.now()): string {
  const ageMs = Math.max(now - new Date(iso).getTime(), 0);
  if (ageMs < HOUR_MS)
    return relativeTimeFormat.format(-Math.round(ageMs / MINUTE_MS), 'minute');
  if (ageMs < DAY_MS)
    return relativeTimeFormat.format(-Math.round(ageMs / HOUR_MS), 'hour');
  return relativeTimeFormat.format(-Math.round(ageMs / DAY_MS), 'day');
}

const FailingChecks = ({
  project,
  severities,
}: {
  project: TeamProjectDto;
  severities: SeverityDto[];
}) => {
  if (project.failingChecks.length === 0) {
    return (
      <StatusBadge tone="success" icon={null} className={styles.severityBadge}>
        None
      </StatusBadge>
    );
  }
  const counts = failingCountsBySeverity(project);
  return (
    <Flex align="center" gap="2" style={{ flexWrap: 'wrap' }}>
      {severities
        .filter(sv => (counts[sv.id] ?? 0) > 0)
        .map(sv => {
          const { label, colorVar, bgVar, fgVar } = severityDisplay(sv);
          const description = `${counts[sv.id]} ${label}`;
          return (
            <Badge
              key={sv.id}
              size="medium"
              role="img"
              aria-label={description}
              title={description}
              className={styles.severityBadge}
              style={{
                background: bgVar,
                border: `1px solid ${colorVar}`,
                color: fgVar,
              }}
            >
              {counts[sv.id]} {label}
            </Badge>
          );
        })}
    </Flex>
  );
};

const LastChecked = ({ project }: { project: TeamProjectDto }) => (
  <Flex
    direction="column"
    gap="0.5"
    title={project.stale ? 'Stale' : undefined}
  >
    <Text
      as="span"
      variant="body-small"
      className={project.stale ? styles.stale : undefined}
    >
      {dateTimeFormat.format(new Date(project.lastCheckedAt))}
    </Text>
    <Text
      as="span"
      variant="body-small"
      color={project.stale ? undefined : 'secondary'}
      className={project.stale ? styles.stale : undefined}
    >
      {formatAge(project.lastCheckedAt)}
      {project.stale && <span className={styles.srOnly}> (Stale)</span>}
    </Text>
  </Flex>
);

interface CategoryFilterItem {
  id: string;
  name: string;
}

interface ProjectsTableProps {
  projects: TeamProjectDto[];
  categoryFilters?: Set<string>;
  categoryFilterItems?: CategoryFilterItem[];
  onRemoveCategoryFilter?: (id: string) => void;
  onClearAllCategoryFilters?: () => void;
}

export const ProjectsTable = ({
  projects,
  categoryFilters,
  categoryFilterItems,
  onRemoveCategoryFilter,
  onClearAllCategoryFilters,
}: ProjectsTableProps) => {
  const entityRoute = useRouteRef(entityRouteRef);
  const { severities } = useSeverities();
  const [query, setQuery] = useState('');
  const [columnSort, setColumnSort] = useState<SortDescriptor | null>(null);

  const rows: ProjectRow[] = useMemo(() => {
    let filtered = projects.filter(p => matchesSearch(p, query));
    if (categoryFilters && categoryFilters.size > 0) {
      filtered = filtered.filter(p =>
        p.failingChecks.some(
          c => c.category !== null && categoryFilters.has(c.category),
        ),
      );
    }
    if (columnSort) {
      const orderedSeverities = [...severities].sort((a, b) => a.rank - b.rank);
      filtered = [...filtered].sort((a, b) => {
        let cmp = 0;
        if (columnSort.column === 'project') {
          cmp = a.projectName.localeCompare(b.projectName);
        } else if (columnSort.column === 'progress') {
          cmp = passPct(a) - passPct(b);
        } else if (columnSort.column === 'failing') {
          const ca = failingCountsBySeverity(a);
          const cb = failingCountsBySeverity(b);
          for (const sv of orderedSeverities) {
            cmp = (ca[sv.id] ?? 0) - (cb[sv.id] ?? 0);
            if (cmp !== 0) break;
          }
        } else if (columnSort.column === 'lastChecked') {
          cmp = a.lastCheckedAt.localeCompare(b.lastCheckedAt);
        }
        return columnSort.direction === 'descending' ? -cmp : cmp;
      });
    }
    return filtered.map(p => ({ ...p, id: p.projectPath }));
  }, [projects, query, columnSort, categoryFilters, severities]);

  const columnConfig: ColumnConfig<ProjectRow>[] = useMemo(() => {
    const entityHref = (ref: string) => {
      try {
        return `${entityRoute(entityRouteParams(ref))}/scorecards`;
      } catch {
        return undefined;
      }
    };
    return [
      {
        id: 'project',
        label: 'Project',
        isRowHeader: true,
        isSortable: true,
        cell: row => {
          const href = row.catalogEntityRef
            ? entityHref(row.catalogEntityRef)
            : undefined;
          return (
            <Cell>
              <Flex align="center" gap="1.5">
                {href ? (
                  <Link
                    href={href}
                    variant="body-medium"
                    truncate
                    title={row.projectName}
                  >
                    {row.projectName}
                  </Link>
                ) : (
                  <Text
                    as="p"
                    variant="body-medium"
                    truncate
                    title={row.projectName}
                  >
                    {row.projectName}
                  </Text>
                )}
                {!row.catalogEntityRef && (
                  <TooltipTrigger>
                    <ButtonIcon
                      aria-label="Not onboarded to Infinity Portal"
                      variant="tertiary"
                      size="small"
                      icon={<RiInformationLine />}
                      className={styles.infoIcon}
                    />
                    <Tooltip>Not onboarded to Infinity Portal</Tooltip>
                  </TooltipTrigger>
                )}
              </Flex>
            </Cell>
          );
        },
      },
      {
        id: 'progress',
        label: 'Progress (passed / total)',
        isSortable: true,
        cell: row => (
          <Cell>
            <Flex direction="column" gap="1.5">
              <Flex align="baseline" gap="1">
                <Text as="span" variant="body-small" weight="bold">
                  {passPct(row)}%
                </Text>
                <Text as="span" variant="body-small" color="secondary">
                  {row.passed} / {row.total}
                </Text>
              </Flex>
              <PassFailBar
                passing={row.passed}
                failing={row.total - row.passed}
                total={row.total}
              />
            </Flex>
          </Cell>
        ),
      },
      {
        id: 'failing',
        label: 'Failing checks',
        isSortable: true,
        cell: row => (
          <Cell>
            <FailingChecks project={row} severities={severities} />
          </Cell>
        ),
      },
      {
        id: 'lastChecked',
        label: 'Last checked',
        isSortable: true,
        cell: row => (
          <Cell>
            <LastChecked project={row} />
          </Cell>
        ),
      },
    ];
  }, [entityRoute, severities]);

  return (
    <section aria-labelledby="team-projects-heading">
      <Flex direction="column" gap="4">
        <Flex gap="3" justify="between" align="center">
          <Text as="h2" id="team-projects-heading" variant="title-small">
            Projects
          </Text>
          <Flex align="center" gap="2">
            {categoryFilterItems && categoryFilterItems.length > 0 && (
              <Flex align="center" gap="2">
                <TagGroup
                  aria-label="Active category filters"
                  onRemove={keys => {
                    keys.forEach(k => onRemoveCategoryFilter?.(String(k)));
                  }}
                >
                  {categoryFilterItems.map(item => (
                    <Tag
                      key={item.id}
                      id={item.id}
                      size="small"
                      textValue={item.name}
                    >
                      {item.name}
                    </Tag>
                  ))}
                </TagGroup>
                {categoryFilterItems.length > 1 && (
                  <button
                    type="button"
                    className={styles.clearAllFilters}
                    onClick={onClearAllCategoryFilters}
                  >
                    Clear all
                  </button>
                )}
              </Flex>
            )}
            <Box>
              <SearchField
                value={query}
                onChange={setQuery}
                aria-label="Search projects"
                placeholder="Search projects"
              />
            </Box>
          </Flex>
        </Flex>
        <Table
          columnConfig={columnConfig}
          data={rows}
          pagination={{ type: 'none' }}
          sort={{ descriptor: columnSort, onSortChange: setColumnSort }}
          emptyState={
            <Text as="p" color="secondary">
              {categoryFilters && categoryFilters.size > 0
                ? 'No failing projects match the selected categories.'
                : 'No projects match your search.'}
            </Text>
          }
        />
      </Flex>
    </section>
  );
};
